import React, { useContext, useState, useEffect } from 'react';
import { useEmptyCallback } from 'utilities/hooks.util';
import {
  DataTable,
  DataTableSkeleton,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableCell,
  Pagination,
  PaginationSkeleton,
  DenormalizedRow,
  DataTableCell,
  DataTableCustomRenderProps
} from 'carbon-components-react';
import { TrashCan16 } from '@carbon/icons-react';
import Util from 'utilities/helper.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import { getOrganisationTransactionLimitsStart } from '../../localStore/actions';

const PageSizes = [10, 20, 50];

const TransactionLimitsList: React.FC<Props> = ({
  organisationId,
  refreshCounter,
  setPendingRemoveRestrictionLimitId
}) => {
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const { organisationTransactionLimits, getOrgTransactionLimitsStatus } =
    localStore;

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const getTransactionLimitsData = useEmptyCallback(
    (organisationId: number, params: any) => {
      localDispatch(
        getOrganisationTransactionLimitsStart({ organisationId, params })
      );
    }
  );

  const renderCell = (cell: DataTableCell, row: DenormalizedRow) => {
    switch (cell.info.header) {
      case 'createdAt':
      case 'expiredAt': {
        const startDate = Util.getNewDate(cell.value)
          .add(1, 's')
          .subtract(24, 'h');
        return Util.toLocalDateFormat(startDate);
      }
      case 'action':
        return (
          <TrashCan16
            className="trash-icon"
            onClick={() => setPendingRemoveRestrictionLimitId(Number(row.id))}
          />
        );
      default:
        return cell.value;
    }
  };

  useEffect(() => {
    const queryParams = {
      fields: 'id,amount,expired_at,created_at,reference,used',
      limit: pageSize,
      order: '-created_at',
      offset: pageOffset
    };
    getTransactionLimitsData(organisationId, queryParams);
  }, [
    pageSize,
    pageOffset,
    organisationId,
    refreshCounter,
    getTransactionLimitsData
  ]);

  useEffect(() => {
    if (organisationTransactionLimits) {
      setTotalItems(organisationTransactionLimits.count);
      const filteredList = organisationTransactionLimits.data?.map(
        (limit: any) => {
          const left = limit.amount - limit.used;
          const transactionsLeft =
            left <= 0
              ? `Used all ${limit.amount}`
              : `Used ${limit.used}/${limit.amount}`;
          return {
            id: limit.id?.toString(),
            createdAt: limit.created_at,
            reference: limit.reference,
            transactionsLeft: transactionsLeft,
            expiredAt: limit.expired_at,
            action: 'remove'
          };
        }
      );
      setFilteredList(filteredList);
    }
  }, [organisationTransactionLimits, getOrgTransactionLimitsStatus]);

  const headers = [
    { key: 'createdAt', header: 'Created date' },
    { key: 'reference', header: 'Reference' },
    { key: 'transactionsLeft', header: 'Number of transactions' },
    { key: 'expiredAt', header: 'End date' },
    { key: 'action', header: '' }
  ];

  return (
    <div className="transaction-limits-list">
      {getOrgTransactionLimitsStatus !== 'fetching' ? (
        <>
          <DataTable rows={filteredList} headers={headers} size="sm">
            {({
              rows,
              getTableProps,
              getRowProps,
              getHeaderProps
            }: DataTableCustomRenderProps) => {
              return (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map((header, i) => (
                          <TableHeader {...getHeaderProps({ header })} key={i}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <>
                        {rows.length > 0 ? (
                          <>
                            {rows.map((row: any) => {
                              return (
                                <TableRow
                                  {...getRowProps({ row })}
                                  key={row.id}
                                >
                                  {row.cells.map((cell: any) => (
                                    <TableCell key={cell.id}>
                                      {renderCell(cell, row)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </>
                        ) : (
                          <TableRow>
                            <TableCell colSpan={headers.length - 1}>
                              No Record
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            }}
          </DataTable>
          {totalItems > pageSize && (
            <Pagination
              size="sm"
              onChange={(data) => handlePageChange(data)}
              backwardText="Previous page"
              forwardText="Next page"
              itemsPerPageText="Display:"
              page={currentPage}
              pageNumberText="Page Number"
              pageSize={pageSize}
              pageSizes={PageSizes}
              totalItems={totalItems}
            />
          )}
        </>
      ) : (
        <>
          <DataTableSkeleton
            headers={headers}
            rowCount={1}
            showHeader={false}
            showToolbar={false}
          />
          {totalItems > pageSize && <PaginationSkeleton />}
        </>
      )}
    </div>
  );
};

type Props = {
  organisationId: number;
  refreshCounter: number;
  setPendingRemoveRestrictionLimitId: Function;
};

export default React.memo(TransactionLimitsList);
